import React, { useContext, useRef, useState } from "react";

import {
    WrapperAdvancedSearch,
    SectionHeading,
    TableCustom,
} from "../../components/common";
import DataTaggerModalApi from "../../components/controls/DataTaggerModalApi";
import { useEntidad, useLista, useFilterPersistence, useNavigateWithFilters } from "../../components/hooks";
import { createSearchWithSavedFilters } from "../../utils/searchWithSavedFilters";
import { usePermissions } from "../../components/hooks/usePermissions";
import { APIS } from "../../config/apis";
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { PERMISSIONS } from "../../consts/permissions";
import { ServerRequest } from "../../utils/apiweb";
import { getDateSerialize, getDateToString } from "../../utils/convert";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { ManagedContext } from "../../context/custom";
import {TIPS} from "../../consts/tips";

const PagoContadoDefinicionesView = () => {
    const { showMessageModal, setIsLoading } = useContext(ManagedContext);

    const [state, setState] = useState({
        loading: false,
        list: []
    });
    const [tableVisible, setTableVisible] = useState(false);
    const [dataTaggerState, setDataTaggerState] = useState({});

    const searchRef = useRef();
    const tableRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.PAGO_CONTADO_DEFINICION_EDIT);

    const [getEntidades, _getEntidad] = useEntidad({
        entidades: ["TipoPlanPago", "Tasa", "SubTasa"],
        onLoaded: (entidades, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: "PagoContadoDefinicionesEntidades",
            timeout: 0,
        },
    });
    const getEntidad = (type, id) => _getEntidad(type, id) ?? {};

    const [getLista, _getRowLista] = useLista({
        listas: ["TipoTributo", "EstadoPagoContadoDefinicion"],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
        },
        memo: {
            key: "PagoContadoDefinicionesListas",
            timeout: 0,
        },
    });
    const getRowLista = (lista, id) => _getRowLista(lista, id) ?? {};

    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setState(prevState => ({...prevState, loading: false}));
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                setState(prevState => ({...prevState, loading: false}));
            });
    };

    const callbackError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error.message);
        setState(prevState => ({...prevState, loading: false}));
    };

    const SearchPagosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.PAGO_CONTADO_DEFINICION,
        buildParamsUrl: (filters) => {
            return `/filter?` +
                `idTipoPlanPago=${filters.idTipoPlanPago || 0}&` +
                `idTipoTributo=${filters.idTipoTributo || 0}&` +
                `idTasaPagoContado=${filters.idTasa || 0}&` +
                `idSubTasaPagoContado=${filters.idSubTasa || 0}&` +
                `idEstadoPagoContadoDefinicion=${filters.idEstadoPagoContadoDefinicion || 0}&` +
                `fechaDesde=${encodeURIComponent(getDateSerialize(filters.fechaDesde || null))}&` +
                `fechaHasta=${encodeURIComponent(getDateSerialize(filters.fechaHasta || null))}&` +
                `descripcion=${filters.descripcion || ''}&` +
                `etiqueta=${filters.etiqueta || ''}`;
        },
        callbackNoSuccess,
        callbackError,
        onSuccess: () => {
            setState(prevState => {
                const list = prevState.list.map(x => {
                    if (x.fechaCreacion) x.fechaCreacion = new Date(x.fechaCreacion);
                    if (x.fechaDesde) x.fechaDesde = new Date(x.fechaDesde);
                    if (x.fechaHasta) x.fechaHasta = new Date(x.fechaHasta);
                    if (x.fechaDesdeAlcanceTemporal) x.fechaDesdeAlcanceTemporal = new Date(x.fechaDesdeAlcanceTemporal);
                    if (x.fechaHastaAlcanceTemporal) x.fechaHastaAlcanceTemporal = new Date(x.fechaHastaAlcanceTemporal);
                    return x;
                });
                return {...prevState, list};
            });
        }
    });

    const { getCurrentFilters, restoredPageIndex, restoredGlobalFilter } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchPagosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    // AVMR actions
    const avmr = {
        onAdd: () => {
            navigateWithFilters(`/pago-contado-definicion/${OPERATION_MODE.NEW}`);
        },
        onView: ({ id }) => {
            navigateWithFilters(`/pago-contado-definicion/${OPERATION_MODE.VIEW}/${id}`);
        },
        onModify: ({ id }) => {
            navigateWithFilters(`/pago-contado-definicion/${OPERATION_MODE.EDIT}/${id}`);
        },
        onClone: ({ id }) => {
            const pago = state.list.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.CLONAR,
                message: `Se va a crear una copia de la definición de pago contado ${pago.codigo}. ¿Querés continuar?`,
                confirmLabel: "Continuar",
                onConfirm: () => cloneId(id),
            });
        },
        onRemove: ({ id }) => {
            const pago = state.list.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la definición de pago contado ${pago.codigo}?`,
                onConfirm: () => removeId(id),
            });
        },
        onAdditionalInfo: ({ id }, disabled) => {
            setDataTaggerState({ id, disabled });
        },
    };

    const removeId = (id) => {
        setState(prevState => ({...prevState, loading: true}));
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.PAGO_CONTADO_DEFINICION,
            `/${id}`,
            null,
            () => {
                setState(prevState => ({
                    ...prevState,
                    loading: false,
                    list: prevState.list.filter((x) => x.id !== id)
                }));
            },
            callbackNoSuccess,
            callbackError
        );
    };

    const cloneId = (id) => {
        setState(prevState => ({...prevState, loading: true}));
        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.PAGO_CONTADO_DEFINICION,
            `/clone/${id}`,
            null,
            () => {
                setState(prevState => ({...prevState, loading: false}));
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Definición de pago contado clonada');
                onSearch();
            },
            callbackNoSuccess,
            callbackError
        );
    };

    const onSearch = () => {
        SearchPagosWithSavedFilters(getCurrentFilters());
    };

    const subTasaCell = ({ value }) => {
        const item = getEntidad("SubTasa", value);
        if (item.codigo && item.descripcion) return `${item.codigo} - ${item.descripcion}`;
        else return "";
    };

    const dataTaggerRegistro = state.list.find(x => x.id === dataTaggerState.id)?.descripcion ?? ''

    return (
        <>
            <SectionHeading
                titles={[
                    { title: "Financiación" },
                    { title: "Definición de pagos contado" },
                ]}
                onAddClick={canEdit ? avmr.onAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Convenio",
                            tipName: TIPS.NAME.MODALS.TIPO_PLAN_PAGO_MODAL,
                            field: "idTipoPlanPago",
                            type: "entity",
                            entidad: "TipoPlanPago",
                            size: 4,
                            columns: [
                                { Header: "Código", accessor: "codigo", width: "30%" },
                                { Header: "Nombre", accessor: "nombre", width: "70%" },
                            ],
                        },
                        {
                            title: "Tipo de tributo",
                            field: "idTipoTributo",
                            type: "list",
                            lista: "TipoTributo",
                            size: 2,
                        },
                        {
                            title: "Estado",
                            field: "idEstadoPagoContadoDefinicion",
                            type: "selectLista",
                            lista: "EstadoPagoContadoDefinicion",
                            size: 2,
                        },
                        {
                            title: "Etiqueta",
                            field: "etiqueta",
                            type: "string",
                            size: 4,
                            searchOnEnter: true,
                        },
                        {
                            title: "Tasa",
                            field: "idTasa",
                            type: "tasa",
                            size: 4,
                        },
                        {
                            title: "Sub tasa",
                            field: "idSubTasa",
                            type: "subtasa",
                            size: 4,
                            fieldTasa: "idTasa",
                        },
                        {
                            title: "Fecha desde",
                            field: "fechaDesde",
                            type: "date",
                            size: 2,
                        },
                        {
                            title: "Fecha hasta",
                            field: "fechaHasta",
                            type: "date",
                            size: 2,
                            minValueField: "fechaDesde",
                        },
                        {
                            title: "Descripción",
                            field: "descripcion",
                            type: "string",
                            size: 8,
                            searchOnEnter: true,
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className={"TableCustomBase m-top-20"}
                        data={state.list}
                        messageEmpty="No se encontraron resultados"
                        columns={[
                            {
                                Header: "Plan pago",
                                accessor: "idTipoPlanPago",
                                Cell: ({ value }) => getEntidad("TipoPlanPago", value).codigo ?? "",
                                width: "10%",
                            },
                            {
                                Header: "Tipo tributo",
                                accessor: "idTipoTributo",
                                Cell: ({ value }) => getRowLista("TipoTributo", value).nombre ?? "",
                                width: "10%",
                            },
                            {
                                Header: "Tasa",
                                accessor: "idTasaPagoContado",
                                Cell: ({ value }) => getEntidad("Tasa", value).codigo ?? "",
                                width: "10%",
                            },
                            {
                                Header: "Sub tasa",
                                accessor: "idSubTasaPagoContado",
                                Cell: subTasaCell,
                                width: "10%",
                            },
                            { Header: "Código", accessor: "codigo", width: "10%" },
                            { Header: "Descripción", accessor: "descripcion", width: "10%" },
                            {
                                Header: "Desde",
                                accessor: "fechaDesde",
                                Cell: ({ value }) => getDateToString(value, false),
                                width: "7%",
                            },
                            {
                                Header: "Hasta",
                                accessor: "fechaHasta",
                                Cell: ({ value }) => getDateToString(value, false),
                                width: "7%",
                            },
                            {
                                Header: "Estado",
                                accessor: "idEstadoPagoContadoDefinicion",
                                Cell: ({ value }) =>
                                    getRowLista("EstadoPagoContadoDefinicion", value).nombre ?? "",
                                width: "7%",
                            },
                        ]}
                        avmr={avmr}
                        editCode={PERMISSIONS.PAGO_CONTADO_DEFINICION_EDIT}
                        restoredPageIndex={restoredPageIndex}
                        defaultGlobalFilter={restoredGlobalFilter}
                    />
                )}

                {dataTaggerState.id && (
                    <DataTaggerModalApi
                        title="Información adicional de pago contado"
                        registro={dataTaggerRegistro}
                        tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_PAGO_CONTADO_MODAL}
                        entidad="PagoContadoDefinicion"
                        idEntidad={dataTaggerState.id}
                        disabled={dataTaggerState.disabled}
                        onConfirm={() => setDataTaggerState({})}
                        onDismiss={() => setDataTaggerState({})}
                    />
                )}
            </section>
        </>
    );
};

export default PagoContadoDefinicionesView;
